import {Component, EventEmitter, Input, Output} from '@angular/core';
import {EventFormAnswer, EventFormQuestion} from '../api/event-api.service';

/**
 * Attendee side of organizer-defined registration questions: renders each question as a radio group
 * (SINGLE_SELECT) or checkbox group (MULTI_SELECT) and emits the answers array on every change.
 * Validation is left to the parent (see missingRequiredLabels) so it can reuse its own submit flow.
 */
@Component({
    selector: 'app-question-fields',
    templateUrl: './question-fields.component.html'
})
export class QuestionFieldsComponent {
    @Input() questions: EventFormQuestion[] = [];
    @Input() answers: EventFormAnswer[] = [];
    /** Unique per form instance: register-event and event-home can both be on screen history. */
    @Input() idPrefix = 'q';
    /** True after the first submit attempt, so unanswered required questions show their error. */
    @Input() showErrors = false;
    @Output() answersChange = new EventEmitter<EventFormAnswer[]>();

    selectedIds(q: EventFormQuestion): string[] {
        const a = (this.answers || []).find(x => x.questionId === q.questionId);
        return a ? a.selectedOptionIds : [];
    }

    isSelected(q: EventFormQuestion, optionId: string): boolean {
        return this.selectedIds(q).includes(optionId);
    }

    hasError(q: EventFormQuestion): boolean {
        return this.showErrors && q.required && this.selectedIds(q).length === 0;
    }

    toggle(q: EventFormQuestion, optionId: string): void {
        const current = this.selectedIds(q);
        let next: string[];
        if (q.type === 'MULTI_SELECT') {
            next = current.includes(optionId) ? current.filter(id => id !== optionId) : [...current, optionId];
        } else {
            next = [optionId];
        }
        this.emit(q, next);
    }

    private emit(q: EventFormQuestion, selectedOptionIds: string[]): void {
        const labels = selectedOptionIds.map(id => q.options.find(o => o.optionId === id)?.label || '');
        const others = (this.answers || []).filter(a => a.questionId !== q.questionId);
        const updated: EventFormAnswer = {
            questionId: q.questionId!,
            questionLabel: q.label,
            selectedOptionIds,
            selectedLabels: labels
        };
        // Keep the organizer's question order so the payload reads naturally.
        const merged = [...others, updated];
        const order = (this.questions || []).map(x => x.questionId);
        merged.sort((a, b) => order.indexOf(a.questionId) - order.indexOf(b.questionId));
        this.answersChange.emit(merged);
    }
}
